import { STUDY_STATUS } from '../utils/decisionTree';

const schoolsCache = {};

function normalize(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function normalizePostcode(value) {
  return String(value || '').toUpperCase().replace(/\s+/g, '').trim();
}

function getDataUrl(fileName) {
  const base = process.env.BASE_URL || '/';
  return `${base.replace(/\/$/, '')}/data/${fileName}`;
}

function getSchoolDataFile(studyStatus) {
  return studyStatus === STUDY_STATUS.FINISHED ? 'schools-2025.json' : 'schools-2026.json';
}

async function loadSchools(studyStatus) {
  const fileName = getSchoolDataFile(studyStatus);
  if (schoolsCache[fileName]) return schoolsCache[fileName];
  const response = await fetch(getDataUrl(fileName));
  if (!response.ok) throw new Error('Unable to load school data');
  schoolsCache[fileName] = await response.json();
  return schoolsCache[fileName];
}

function getSchoolStatus(record) {
  const provisional = String(record.PROVISIONAL || record.provisional || 'N').toUpperCase();
  const aspiring = String(record.ASSC_STATUS || record.asscStatus || record.aspiring || 'N').toUpperCase();

  // Deck logic:
  // PROVISIONAL = Y means the cycle status is not yet confirmed regardless of ASSC_STATUS.
  // Only PROVISIONAL = N and ASSC_STATUS = Y is eligible/aspiring.
  if (provisional === 'Y') return 'unconfirmed';
  if (aspiring === 'Y') return 'aspiring-state';
  return 'not-eligible';
}

function buildDisplayName(name, town, postcode) {
  const baseName = [name, town].filter(Boolean).join(', ');
  return [baseName, postcode].filter(Boolean).join(', ');
}

function mapSchool(record) {
  const schoolName = record.SCL_NAME || record.schoolName || record.name || record.displayName;
  const town = record.SCL_ADD4 || record.SCL_ADD3 || record.townOrCity || record.town || '';
  const postcode = record.SCL_PCOD || record.postcode || '';
  const displayBase = record.displayName || buildDisplayName(schoolName, town, '');

  return {
    id: record.id || `${record.SCL_CODE || record.applyCentre || schoolName}-${postcode}`,
    applyCentre: record.SCL_CODE || record.applyCentre,
    name: schoolName,
    displayName: buildDisplayName(displayBase, '', postcode),
    town,
    postcode,
    year: record.year,
    cycle: record.CYCLE,
    asscStatus: String(record.ASSC_STATUS || 'N').toUpperCase(),
    provisional: String(record.PROVISIONAL || 'N').toUpperCase(),
    type: getSchoolStatus(record),
    raw: record
  };
}

export async function searchSchools(query, studyStatus) {
  const searchTerm = normalize(query);
  const searchPostcode = normalizePostcode(query);

  if (!searchTerm || searchTerm.length < 2) return [];

  const schools = await loadSchools(studyStatus);

  return schools
    .map(mapSchool)
    .filter((school) => {
      const searchableText = normalize([
        school.name,
        school.displayName,
        school.town,
        school.postcode,
        school.applyCentre
      ].filter(Boolean).join(' '));

      const schoolPostcode = normalizePostcode(school.postcode);
      return searchableText.includes(searchTerm) || schoolPostcode.includes(searchPostcode);
    })
    .slice(0, 10);
}

export async function findSchoolById(id, studyStatus) {
  const schools = await loadSchools(studyStatus);
  const match = schools.map(mapSchool).find((school) => school.id === id);
  return match || null;
}

export default { searchSchools, findSchoolById };
